import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Folder, List } from 'lucide-react';
import AdminLayout from '@/components/layout/AdminLayout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import BlogPostsTab from '@/components/admin/blog/BlogPostsTab';
import BlogGroupsTab from '@/components/admin/blog/BlogGroupsTab';
import axios from 'axios';

// Added interface for blog groups to ensure type safety
interface BlogGroup {
  id?: string;
  name: string;
  description: string | null;
  sort_order: number;
}

// Added interface for blog posts to ensure type safety
interface BlogPost {
  id?: string;
  title: string;
  content: string;
  excerpt: string;
  author: string;
  read_time: string;
  group_id: string | null;
  image: string;
  created_at?: string;
  group_name?: string;
  date?: string; // Added the date property that was missing
}

const BlogManagement = () => {
  // Blog Posts State
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [blogGroups, setBlogGroups] = useState<BlogGroup[]>([]);

  // UI State
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('posts');

  useEffect(() => {
    fetchBlogGroups();
    // 组件加载时可以设置初始状态或执行其他操作
  }, [searchParams]);

  const fetchBlogGroups = async () => {
    try {
      const response = await axios.get('/api/blog-groups');
      const data = response.data;
      setBlogGroups(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching blog groups:', error);
      setBlogGroups([]);
      toast({
        title: "Error",
        description: "Failed to load blog groups",
        variant: "destructive"
      });
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex items-center justify-between mb-4">
            <TabsList className="grid w-full max-w-md grid-cols-2">
              <TabsTrigger value="posts">Blog Posts</TabsTrigger>
              <TabsTrigger value="groups">Blog Groups</TabsTrigger>
            </TabsList>
          </div>
          
          <TabsContent value="posts" className="space-y-4">
            <BlogPostsTab
              blogPosts={blogPosts}
              setBlogPosts={setBlogPosts}
              blogGroups={blogGroups}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
            />
          </TabsContent>
          
          <TabsContent value="groups" className="space-y-4">
            <BlogGroupsTab
              blogGroups={blogGroups}
              setBlogGroups={setBlogGroups}
            />
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default BlogManagement;
